import React, { useState } from 'react';
import { useToast } from '@crisellevaldez/speculo-ui';
import { RemiSetupData, TwilioSetupData } from '../schemas/onboarding.schema';
import { RemiSetupStep } from './RemiSetupStep';
import { TwilioSetupStep } from './TwilioSetupStep';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useUpdateOrganizationSettings } from '../../../../hooks/organizations/useUpdateOrganizationSettings';
import { useQueryClient } from '@tanstack/react-query';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { useWarnBeforeUnload } from '../hooks/useWarnBeforeUnload';
import { getConfirmedSave, OrganizationSaveResponse } from '../utils/confirmStageSave';
import { SAVE_NOT_CONFIRMED_MESSAGE } from '../utils/savingMessages';
import { invalidateOrganizationQueries } from '../../../../utils/cacheUtils';

interface Stage3RemiSetupProps {
  onNext: (data: RemiSetupData & { twilioData?: TwilioSetupData }) => void;
  initialData?: Partial<RemiSetupData>;
  initialTwilioData?: Partial<TwilioSetupData>;
  isLoading?: boolean;
  organizationName?: string;
  officeAddress?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: 'US' | 'Canada';
  };
}

export const Stage3RemiSetup: React.FC<Stage3RemiSetupProps> = ({
  onNext,
  initialData,
  initialTwilioData,
  isLoading = false,
  organizationName = '',
  officeAddress,
}) => {
  const { organization, setOrganization } = useOrganizationStore();
  const { updateSettings } = useUpdateOrganizationSettings();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // State for 2-step flow: remi -> twilio
  const [currentStep, setCurrentStep] = useState<'remi' | 'twilio'>(() => {
    // Check if remi data was saved (remiCompleted flag) - if so, go to twilio
    if ((organization as any)?.onboardingData?.remi_completed) {
      return 'twilio';
    }
    return 'remi';
  });

  const [savedRemiData, setSavedRemiData] = useState<RemiSetupData | null>(null);
  const [isSavingRemi, setIsSavingRemi] = useState(false);
  useWarnBeforeUnload(isSavingRemi);
  const { sendWebhook } = useOnboardingWebhook();

  const handleRemiSubmit = async (data: RemiSetupData) => {
    if (!organization?.id) return;

    setIsSavingRemi(true);
    try {
      // Save remi data to organization
      const saveResponse = await updateSettings({
        id: organization.id,
        data: {
          remi_intro_name: data.remi_intro_name,
          assistant_name: data.assistant_name,
          assistant_name_pronunciation: data.assistant_name_pronunciation,
          call_settings: {
            voice: data.voice || '',
            background_track: data.background_track || '',
            record: true,
          },
          onboardingData: {
            ...(useOrganizationStore.getState().organization as any)?.onboardingData ?? {},
            remi: {
              kb_name: data.kb_name,
              service_type: data.service_type,
              service_areas: data.service_areas,
              why_work_with_you: data.why_work_with_you,
              // Kept so the knowledge base still has it if the page reloads before the phone step.
              website_url: data.website_url,
              custom_questions: data.custom_questions,
            },
            remi_completed: true,
          },
        } as any,
      });

      // The response is the committed row; advance once it shows the Remi step saved.
      const confirmed = getConfirmedSave(
        saveResponse as OrganizationSaveResponse,
        (_setup, data) => data.remi_completed === true
      );
      if (!confirmed) throw new Error(SAVE_NOT_CONFIRMED_MESSAGE);
      const current = useOrganizationStore.getState().organization;
      if (current) setOrganization({ ...current, ...confirmed.patch });
      invalidateOrganizationQueries(queryClient);

      // Save data locally and proceed to Twilio step
      setSavedRemiData(data);

      showToast({
        message: "Remi settings saved. Now let's set up your phone number.",
        variant: 'success',
      });

      setCurrentStep('twilio');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving Remi setup:', error);
      showToast({
        message:
          error instanceof Error && error.message === SAVE_NOT_CONFIRMED_MESSAGE
            ? SAVE_NOT_CONFIRMED_MESSAGE
            : 'Failed to save settings. Please try again.',
      });
    } finally {
      setIsSavingRemi(false);
    }
  };

  const handleTwilioSubmit = async (data: TwilioSetupData) => {
    // Combine remi data with twilio data
    const combinedData = {
      ...(savedRemiData || (initialData as RemiSetupData)),
      twilioData: data,
    };

    // Send webhook for Twilio setup only (not Remi voice data)
    sendWebhook('Stage 3 - Twilio Setup Complete', data);

    // Pass to parent
    onNext(combinedData);
  };

  const handleBackToRemi = () => {
    setCurrentStep('remi');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // STEP 2: Twilio Setup
  if (currentStep === 'twilio') {
    return (
      <TwilioSetupStep
        onNext={handleTwilioSubmit}
        onBack={handleBackToRemi}
        initialData={initialTwilioData}
        isLoading={isLoading}
        officeAddress={officeAddress}
      />
    );
  }

  // STEP 1: Remi Setup
  return (
    <RemiSetupStep
      onNext={handleRemiSubmit}
      initialData={initialData}
      isLoading={isSavingRemi}
      organizationName={organizationName}
    />
  );
};
